import { Button, Col, Flex, Row, Typography } from "antd";
import { Can } from "@/shared/lib/casl";
import { UserBubble } from "@shared/ui/user-bubble";
import { fallbackString, formatMoscowDatetime } from "@shared/utils";
import { JournalCryptographyWrapper } from "../journal-cryptography-wrapper";
import { statusIcons } from "../journal-signer/model/status-icons";
import "./journal-signers.scss";

const { Text } = Typography;

interface SignerData {
  id: string;
  title: string;
  signerLabel: string;
  currentItem: any;
  signerFio: string;
  isSignerAwaiting: boolean;
  sectionListId?: string;
  itemNoVersionId: string;
  localUser: any;
  signingString: string;
  isSigningPending: boolean;
  getSigningXml: (params: any) => Promise<void>;
  setSigningString: (xml: string) => void;
  withSignedString: (params: any, xml: string, mchdId?: string) => void;
  invalidateItem: () => void;
  openReject: () => void;
  canAction?: any;
}

interface JournalSignersProps {
  title?: string;
  signers: SignerData[];
}

export const JournalSigners = ({
  title = "Подписанты",
  signers = [],
}: JournalSignersProps) => {
  if (signers.length === 0) {
    return null;
  }

  // Находим последнего подписанта для отображения общего статуса
  const lastSigner = signers[signers.length - 1];
  const isRejected = lastSigner?.currentItem?.status === "REJECTED";
  const isSigned = lastSigner?.currentItem?.status === "SIGNED";
  const getMchdNumber = (item: any) =>
    item?.signer?.mchdId ||
    item?.inspectionEmployee?.mchdId ||
    item?.defectFixingInspectionEmployee?.mchdId ||
    item?.mchdId;

  return (
    <div className="journal-signers">
      <div className="journal-signers__title">{title}</div>
      
      {signers.map((signer) => (
        <Row key={signer.id} gutter={[0, 8]} justify="space-between" className="journal-signers__row">
          <Col span={12}>
            <Text type="secondary">{signer.signerLabel}</Text>
          </Col>
          <Col span={12} className="journal-signers__col-right">
            <Flex
              align="center"
              gap="8px"
              style={{ justifyContent: "flex-end" }}
            >
              <UserBubble userId={signer.currentItem?.signer?.userId || signer.currentItem?.inspectionEmployee?.inspectionEmployeeId || signer.currentItem?.defectFixingInspectionEmployee?.defectFixingInspectionEmployeeId || ""}>
                <Typography.Text
                  style={{ color: "#4169E1", cursor: "pointer" }}
                >
                  {fallbackString(signer.signerFio)}
                </Typography.Text>
              </UserBubble>
              <div className="journal-signers__status-icon-slot">
                {statusIcons[signer.currentItem?.status as keyof typeof statusIcons]}
              </div>
            </Flex>
            <div>{fallbackString(signer.currentItem?.signer?.position || signer.currentItem?.inspectionEmployee?.position || signer.currentItem?.defectFixingInspectionEmployee?.position)}</div>
            {signer.currentItem?.status === "SIGNED" && getMchdNumber(signer.currentItem) ? (
              <div className="journal-signers__mchd">
                Доверенность № {getMchdNumber(signer.currentItem)}
              </div>
            ) : null}
            {signer.isSignerAwaiting && signer.sectionListId && (
              <div className="journal-signers__actions">
                <Can I="sign" a={signer.canAction as any}>
                  <JournalCryptographyWrapper
                    currentUser={signer.localUser as any}
                    signContent={signer.signingString}
                    onSuccess={(xml, _proxyId, mchdId) =>
                      signer.withSignedString(
                        {
                          sectionFourListId: signer.sectionListId,
                          itemNoVersionId: signer.itemNoVersionId,
                          signerEmployeeId: signer.localUser?.user_id || "",
                          onSuccess: () => signer.invalidateItem(),
                        },
                        xml,
                        mchdId,
                      )
                    }
                  >
                    {({ onOpen }: any) => (
                      <Button
                        type="primary"
                        loading={signer.isSigningPending}
                        onClick={async () => {
                          try {
                            await signer.getSigningXml({
                              sectionFourListId: signer.sectionListId,
                              itemNoVersionId: signer.itemNoVersionId,
                              signerEmployeeId: signer.localUser?.user_id || "",
                            });
                            onOpen();
                          } catch (e) {
                            // eslint-disable-next-line no-console
                            console.error(e);
                          }
                        }}
                      >
                        Подписать
                      </Button>
                    )}
                  </JournalCryptographyWrapper>
                </Can>
                <Can I="sign" a={signer.canAction as any}>
                  <Button type="dashed" onClick={signer.openReject}>
                    Отклонить
                  </Button>
                </Can>
              </div>
            )}
          </Col>
        </Row>
      ))}

      {isRejected && (
        <div className="journal-signers__status-info journal-signers__status-info--rejected">
          <div className="journal-signers__status-header">
            <div className="journal-signers__status-text">
              <div className="journal-signers__status-icon" />
              <span className="journal-signers__status-title">
              Отклонено подписание
              </span>
            </div>
            <div className="journal-signers__status-date">
              {formatMoscowDatetime(lastSigner?.currentItem?.updateDate)}
            </div>
          </div>
          {lastSigner?.currentItem?.comment && (
            <div className="journal-signers__status-comment">
              {lastSigner.currentItem?.comment}
            </div>
          )}
        </div>
      )}

      {isSigned && (
        <div className="journal-signers__status-info journal-signers__status-info--signed">
          <div className="journal-signers__status-header">
            <div className="journal-signers__status-text">
              <div className="journal-signers__status-icon" />
              <span className="journal-signers__status-title">
              Подписано
              </span>
            </div>
            <div className="journal-signers__status-date">
              {formatMoscowDatetime(lastSigner?.currentItem?.updateDate)}
            </div>
          </div>
          {getMchdNumber(lastSigner?.currentItem) ? (
            <div className="journal-signers__status-mchd">
              Доверенность № {getMchdNumber(lastSigner?.currentItem)}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
